import React from 'react';
import TurbineTableAction from './TurbineTableAction';
import Status from './Status/Status';
import { BulkTurbinesUpdateConfig, TurbineStatus } from './Landing.slice';
import AssigneeColumn from './Assignee/AssigneeColumn';
import { getColumns, Column, ColumnProps } from '../TableOptions/Helpers';
import { generateColumn } from '../../Shared/TableOptions/Helpers';
import { SelectOption } from '../../AnnotationManager/Annotation/annotation.types';

const defaultColumnWidth = '150px';

const ColumnWidths = new Map<string, string>([
  ['blade', '100px'],
  ['stitchingReviewStatus', '200px'],
  ['inspection', '100px'],
  ['turbine', '100px'],
  ['site', '150px'],
  ['assignee', '200px'],
  ['statusText', '200px'],
  ['resolution', '150px']
]);

const getColumnWidth = (column: string) => {
  if (ColumnWidths.has(column)) {
    return ColumnWidths.get(column)!;
  }

  return defaultColumnWidth;
};

const renderStatus = (record: string) => <Status status={record} />;

const turbineColumns: Column[] = [
  generateColumn('actions', 'Actions', false, (record: any): JSX.Element => <TurbineTableAction record={record} />, true),
  generateColumn('site', 'Site', true, undefined, true),
  generateColumn('turbine', 'Turbine', true, undefined, true),
  generateColumn('inspection', 'Inspection', true, undefined, true),
  generateColumn('assignee', 'Assignee', false, (record: any): JSX.Element => <AssigneeColumn turbine={record} />, true),
  generateColumn('statusText', 'Status', true, renderStatus, true),
  generateColumn('resolution', 'Resolution', true, undefined, true),
  generateColumn('highestSeverity', 'Highest Severity', true, undefined, true)
];

const sequenceColumns: Column[] = [
  generateColumn('blade', 'Blade', true, undefined, true),
  generateColumn('bladeSide', 'Blade Side', true, undefined, true),
  generateColumn('status', 'Status', true, renderStatus, true)
];

export const getTurbineColumns = (): ColumnProps[] =>
  turbineColumns.map(x => getColumns(x.key, x.title, x.addDataIndex, getColumnWidth, x.render, x.isVisible));

export const getSequenceColumns = (): ColumnProps[] =>
  sequenceColumns.map(x => getColumns(x.key, x.title, x.addDataIndex, getColumnWidth, x.render, x.isVisible));

const StatusesConfirms = new Map<TurbineStatus, string>([
  [TurbineStatus.WaitingForReview, 'Are you sure you want to start "Annotation review"?'],
  [TurbineStatus.WaitingForQaReview, 'Are you sure you want to start "Annotation QA review"?']
]);

export const getAdditionalConfirm = (status: TurbineStatus) => {
  if (StatusesConfirms.has(status)) {
    return StatusesConfirms.get(status)!;
  }

  return null;
};

export const showStartProgressDialog = (status: TurbineStatus, canReview: boolean | undefined, canQAReview: boolean | undefined) =>
  (canQAReview === true && status === TurbineStatus.WaitingForQaReview) || (canReview === true && status === TurbineStatus.WaitingForReview);

export const whereToNavigate = (location: any) => {
  const annotationManagerMode = location && location.pathname && location.pathname.includes('/annotation-manager');
  const home = annotationManagerMode ? '/annotation-manager' : '/stitching-tool';

  return { home, annotationManagerMode };
};

export const whatKeysToSave = () => {
  if (window.location.pathname.includes('annotation-manager')) {
    return {
      SAVED_HIDDEN_COLUMNS_KEY: 'savedHiddenColumnsAnnotationManager',
      SAVED_COLUMNS_ORDER_KEY: 'savedColumnsOrderAnnotationManager'
    };
  }
  if (window.location.pathname.includes('stitching-tool')) {
    return {
      SAVED_HIDDEN_COLUMNS_KEY: 'savedHiddenColumnsStitchingTool',
      SAVED_COLUMNS_ORDER_KEY: 'savedColumnsOrderStitchingTool'
    };
  } else
    return {
      SAVED_HIDDEN_COLUMNS_KEY: '',
      SAVED_COLUMNS_ORDER_KEY: ''
    };
};

export const possibleBulkUpdateStatuses: SelectOption[] = [
  {
    text: 'Manual stitching in progress',
    value: TurbineStatus.StitchingInProgress.toString()
  },
  {
    text: 'Waiting for annotation review',
    value: TurbineStatus.WaitingForReview.toString()
  },
  {
    text: 'Waiting for annotation qa review',
    value: TurbineStatus.WaitingForQaReview.toString()
  }
];

export const getConfirmText = (model: BulkTurbinesUpdateConfig) => {
  let confirmText = model.assigneeId
    ? `Are you sure you want to assign selected turbines to ${model.assigneeFullName}`
    : `Are you sure you want to remove assignee for selected turbines`;

  if (model.status) {
    const statusText = possibleBulkUpdateStatuses.find(x => x.value === model.status)!.text;

    confirmText = `${confirmText} and set status to ${statusText}`;
  }

  confirmText = `${confirmText}?`;

  return confirmText;
};
